import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';
import { ColorPickerPresets } from './ColorPickerPresets';

export interface ColorSwatchProps {
  color: string;
  disabled?: boolean;
  onClick: (color: string) => void;
  selected: boolean;
  size: number;
  testId: string;
}

const ColorSwatchSize: number = 15;

const ColorSwatchStyleName = TypeStyle.style({
  boxSizing: 'border-box',
  height: ColorSwatchSize,
  width: ColorSwatchSize,
  $nest: {
    '&:hover, &:active, &[aria-selected="true"]': { position: 'relative' },
    '&[aria-selected="true"]::after': { outline: `1px solid ${TabStyles.Colors.L8}` },
    '&:hover::after, &:active::after': { outline: `1px solid ${TabStyles.Colors.L9}` },
    '&:hover::after, &:active::after, &[aria-selected="true"]::after': {
      border: `1px solid white`,
      bottom: -1,
      content: `""`,
      left: -1,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: -1,
    },
  }
});

export const ColorSwatchWidget = React.forwardRef<HTMLDivElement, ColorSwatchProps>((props, ref) => {
  const color = props.color.toLowerCase();
  const handleClick = React.useCallback(() => props.onClick(color), [props.onClick, color]);
  return (
    <div
      {...EventUtilsOptOut}
      aria-selected={props.selected}
      className={TypeStyle.classes(ColorSwatchStyleName, props.disabled && 'disabled')}
      data-tb-test-id={makeTestId(props.testId, 'ColorSwatch', color)}
      onClick={props.disabled ? undefined : handleClick}
      ref={ref}
      style={{
        backgroundColor: color,
        height: props.size,
        width: props.size,
      }}
    />
  );
});
ColorSwatchWidget.displayName = 'ColorSwatchWidget';

export enum ColorGridType {
  Default,
  Minimal,
}

export enum ColorGridPreset {
  Dark,
  Light,
  MaestroFlowColors,
}

export interface ColorGridWidgetProps {
  /**
   * The set of columns that will be displayed in the color grid.
   * @default ColorGridPreset.Dark
   */
  colorsPreset?: ColorGridPreset;

  disabled?: boolean;

  /**
   * Type of color grid widget to render
   */
  gridType?: ColorGridType;

  /**
   * Callback function to notify user when a color swatch is picked
   */
  handleSwatchClicked: (color: string) => void;

  /**
   * Color that is currently selected
   */
  selectedColor?: string;

  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-ColorGridWidget' will be automatically added
   */
  testId: string;
}

function getPreset(presetType?: ColorGridPreset): Readonly<string[]> {
  switch (presetType) {
    default:
    case ColorGridPreset.Dark: return ColorPickerPresets.Dark;
    case ColorGridPreset.Light: return ColorPickerPresets.Light;
    case ColorGridPreset.MaestroFlowColors: return ColorPickerPresets.MaestroFlowColors;
  }
}

const GroupSpacingPx = 3;
const DividerHeightPx = 4;

export class ColorGridWidget extends React.Component<ColorGridWidgetProps> {
  public static defaultProps: Pick<ColorGridWidgetProps, 'colorsPreset'> = { colorsPreset: ColorGridPreset.Dark };
  public render(): JSX.Element {
    return <div data-tb-test-id={makeTestId(this.props.testId, 'ColorGrid')} tabIndex={0}>
      {this.getColorSwatchGridBuilder(this.props.gridType)}
    </div>;
  }

  private getColorSwatchGridBuilder = (gridType?: ColorGridType) => {
    switch (gridType) {
      default:
      case ColorGridType.Default:
        return this.buildDefaultColorSwatchGrid();
      case ColorGridType.Minimal:
        return this.buildMinimalColorSwatchGrid();
    }
  }

  private buildMinimalColorSwatchGrid(): JSX.Element {
    const colorsPerColumn = 4;
    const columns: Object[] = [];
    const colors: Readonly<string[]> = getPreset(this.props.colorsPreset);

    let currentColumn: Object[] = [];
    let currentGroup: Object[] = [];
    let colorsInColumn: number = 0;
    for (let i = 0; i < colors.length; i++) {
      const color: string = colors[i].toLowerCase();
      const colorSwatchProps: ColorSwatchProps & React.Attributes = {
        color,
        onClick: this.props.handleSwatchClicked,
        key: 'swatch-' + color,
        size: ColorSwatchSize,
        selected: this.props.selectedColor === color,
        disabled: this.props.disabled,
        testId: this.props.testId
      };
      currentGroup.push(<ColorSwatchWidget {...colorSwatchProps} />);

      colorsInColumn += 1;

      // when we reached the max number of colors in a column or we are about the add a divider, then we need to flush the group and render
      // as a div with gray borders
      if (colorsInColumn === colorsPerColumn) {
        currentColumn.push(<div children={currentGroup} style={{ border: `1px solid ${TabStyles.Colors.L6}` }} key={'group-' + i} />);

        columns.push(<div
          children={currentColumn}
          key={'column-' + i}
          style={{ float: 'left', marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: (columns.length ? GroupSpacingPx : 0) }}
        />);
        currentColumn = [];
        colorsInColumn = 0;
        currentGroup = [];
      }
    }
    return <div children={columns} style={{ display: 'flex' }} />;
  }

  private buildDefaultColorSwatchGrid(): JSX.Element {
    const colorsPerColumn = 6;
    const columns: Object[] = [];
    const colors: Readonly<string[]> = getPreset(this.props.colorsPreset);

    let currentColumn: Object[] = [];
    let currentGroup: Object[] = [];
    let colorsInColumn: number = 0;
    for (let i = 0; i < colors.length; i++) {
      const color: string = colors[i].toLowerCase();
      const colorSwatchProps: ColorSwatchProps & React.Attributes = {
        color,
        onClick: this.props.handleSwatchClicked,
        key: 'swatch-' + color,
        size: ColorSwatchSize,
        selected: this.props.selectedColor === color,
        disabled: this.props.disabled,
        testId: this.props.testId
      };
      currentGroup.push(<ColorSwatchWidget {...colorSwatchProps} />);

      colorsInColumn += 1;
      // for the first 2 columns (gray colors) we will have a divider after the first color swatch, after that the divider is after the 3rd
      const addDivider: boolean = colorsInColumn === 1 && columns.length < 2 || colorsInColumn === 3 && columns.length >= 2;

      // when we reached the max number of colors in a column or we are about the add a divider, then we need to flush the group and render
      // as a div with gray borders
      if (colorsInColumn === colorsPerColumn || addDivider) {
        currentColumn.push(<div children={currentGroup} style={{ border: `1px solid ${TabStyles.Colors.L6}` }} key={'group-' + i} />);
        if (addDivider) {
          currentColumn.push(<div style={{ width: ColorSwatchSize, height: DividerHeightPx }} key={'divider-' + i} />);
        }

        if (colorsInColumn === colorsPerColumn) {
          columns.push(<div
            children={currentColumn}
            key={'column-' + i}
            style={{ float: 'left', marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: (columns.length ? GroupSpacingPx : 0) }}
          />);
          currentColumn = [];
          colorsInColumn = 0;
        }
        currentGroup = [];
      }
    }
    return <div children={columns} style={{ display: 'flex' }} />;
  }
}
